import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { en, fr } from './i18n';
import AutoPlayInfoIcon from './AutoPlayInfoIcon';
import { useTranslation, Trans } from 'react-i18next';
import TooltipClose from '../common/TooltipCloseSvg';

export interface PdpVideoProps {
    //DomNode: string;
    VideoData: {
        slideElement: Element;
        videoFile: string;
        id: string;
        videoContainerID: string;
    };
    isAutoPlayEnabled: boolean;
    onAutoPlayChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const PdpVideoItem = ({ VideoData, isAutoPlayEnabled, onAutoPlayChange }: PdpVideoProps) => {
    const { i18n, t } = useTranslation('pdp-video');
    i18n.addResourceBundle('en', 'pdp-video', en, true, true);
    i18n.addResourceBundle('fr', 'pdp-video', fr, true, true);
    const { slideElement, videoFile, id, videoContainerID } = VideoData;
    const [isVideoPlaying, setIsVideoPlaying] = useState(false);
    const [ampInstance, setAmpInstance] = useState<any>(null);
    const [showAutoPlayTooltip, setShowAutoPlayTooltip] = useState(false);
    const prevAmp = useRef(null);
    const prevFile = useRef(null);

    useEffect(() => {
        const createAmp = async () => {
            const amp = await akamai.amp.AMP.create(videoContainerID + 'z', {
                autoplay: isAutoPlayEnabled && window.innerWidth > 768,
                background: '#000',
                autoplayPolicy: 'muted',
                temporalType: 'vod',
                media: {
                    //poster: item.thumbnail,
                    src: videoFile,
                    type: 'video/mp4',
                },
                preload: 'auto',
                paths: {
                    player: '/react/components/amp/',
                    resources: '/react/components/amp/',
                    plugins: '/react/components/amp/',
                },
                playsinline: true,
            });

            setIsVideoPlaying(true);
            setAmpInstance(amp);
            //disable gallery swipe detection on progress bar and play button
            const progressBar = document.querySelector('.amp-slide-in-out');
            const ampControls = document.querySelector('.amp-controls');
            const playButton = document.querySelector('.amp-pause-overlay');
            if (progressBar) progressBar.classList.add('swiper-no-swiping');
            if (playButton) playButton.classList.add('swiper-no-swiping');
            if (ampControls) ampControls.classList.add('swiper-no-swiping');
            //add analytics events
            let percentageComplete = [];
            amp.addEventListener(akamai.amp.Events.PLAY_REQUEST, (event) => {
                //console.log('VIDEO START');
                window._satellite && window._satellite.track('VideoStart');
            });
            amp.addEventListener(akamai.amp.Events.PLAY, (event) => {
                //console.log('VIDEO PLAY');
                window._satellite && window._satellite.track('Play');
            });
            amp.addEventListener(akamai.amp.Events.ENDED, (event) => {
                //console.log('VIDEO 100');
                window._satellite && window._satellite.track('Video100Percent');
                percentageComplete = [];
            });
            amp.addEventListener('autoplayblocked', (event) => {
                const mode = event.detail.mode;
                const threshold = event.detail.threshold;
                console.log("Attemted autoplay '" + mode + "', but the browser's threshold is '" + threshold + "'");
            });
            amp.addEventListener(akamai.amp.Events.TIME_UPDATE, (event) => {
                const currentTime = Number(event.player.absoluteCurrentTime);
                const duration = Number(event.player.absoluteDuration);
                const percentViewed = Math.floor((currentTime / duration) * 100);
                switch (true) {
                    case percentViewed == 0:
                        percentageComplete = [];
                        break;
                    case percentViewed == 25:
                        if (!(percentageComplete.indexOf('Video25Percent') > -1)) {
                            //console.log('VIDEO: tracking 25% MILESTONE event');
                            window._satellite && window._satellite.track('Video25Percent');
                            percentageComplete.push('Video25Percent');
                        }
                        break;
                    case percentViewed == 50:
                        if (!(percentageComplete.indexOf('Video50Percent') > -1)) {
                            //console.log('VIDEO: tracking 50% MILESTONE event');
                            window._satellite && window._satellite.track('Video50Percent');
                            percentageComplete.push('Video50Percent');
                        }
                        break;
                    case percentViewed == 75:
                        if (!(percentageComplete.indexOf('Video75Percent') > -1)) {
                            //console.log('VIDEO: tracking 75% MILESTONE event');
                            window._satellite && window._satellite.track('Video75Percent');
                            percentageComplete.push('Video75Percent');
                        }
                        break;
                }
            });
            return amp;
        };

        const initVideo = async () => {
            let ampInst = null;
            if (videoContainerID != null) {
                ampInst = await createAmp();
            }
            if (prevAmp.current != null && prevFile.current != null) {
                //destory previous player instance
                prevAmp.current.destroy();
            }
            if (videoContainerID != null) {
                prevAmp.current = ampInst;
                prevFile.current = videoFile;
            } else {
                prevAmp.current = null;
                prevFile.current = null;
            }
        };
        initVideo();
    }, [videoContainerID, videoFile]);

    // useEffect(() => {
    //     if (stopVideo && ampInstance && ampInstance.pause && typeof ampInstance.pause === 'function') {
    //         ampInstance.pause();
    //     }
    // }, [stopVideo, ampInstance]);
    // useEffect(() => {
    //     if (
    //         isAutoPlayEnabled &&
    //         startVideo &&
    //         ampInstance &&
    //         ampInstance.play &&
    //         typeof ampInstance.play === 'function'
    //     ) {
    //         ampInstance.play();
    //     }
    // }, [isAutoPlayEnabled, startVideo, ampInstance]);

    useEffect(() => {
        return () => {
            if (ampInstance) {
                ampInstance.destroy();
            }
        };
    }, []);

    if (videoContainerID == null) return null;

    return ReactDOM.createPortal(
        <>
            <div className="video-item">
                {/* <pre>{JSON.stringify(VideoData, null, 2)}</pre> */}
                <div
                    id={videoContainerID + 'z'}
                    className="video-item__video"

                    //style={{ width: `${w}px`, height: `${h}px` }}
                ></div>
            </div>
            <p className="video-item__video-desc">{t('videoDesc')}</p>
            <div className="video-item__auto-play swiper-no-swiping">
                <input
                    type="checkbox"
                    id={videoFile + '-auto-play'}
                    checked={isAutoPlayEnabled}
                    onChange={onAutoPlayChange}
                    className="video-item__auto-play__checkbox"
                />
                <label htmlFor={videoFile + '-auto-play'} className="video-item__auto-play__text">
                    {t('autoPlayText')}
                </label>
                <button
                    className="video-item__auto-play__info"
                    onClick={() => {
                        setShowAutoPlayTooltip(true);
                    }}
                >
                    <AutoPlayInfoIcon className="video-item__auto-play__info-icon" />
                </button>
                {showAutoPlayTooltip && (
                    <div className="video-item__auto-play__tooltip swiper-no-swiping">
                        <div className="video-item__auto-play__tooltip-content">
                            <span className="video-item__auto-play__tooltip-content__text">
                                {t('autoPlayDisclaimerText')}
                            </span>
                            <button
                                onClick={() => setShowAutoPlayTooltip(false)}
                                className="video-item__auto-play__tooltip-content__close-button"
                            >
                                <span className="visually-hidden">{t('closeText')}</span>
                                <TooltipClose />
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </>,
        slideElement.querySelector('#' + videoContainerID),
    );
};

export default PdpVideoItem;
